import { useCallback, useEffect, useState } from "react";
import { t } from "i18next";
import { CellClickedEvent, GridApi, GridReadyEvent } from "ag-grid-community";
import { Dialog } from "@livingmap/hippo";

import Grid from "@components/Grid/Grid";
import { AssetOptions } from "@components/Grid/components/OptionsButtonCellRenderer";
import UnsavedChanges from "../../Modal/UnsavedChanges";

import { useAppSelector, useAppDispatch } from "@redux/hooks";
import {
  setFeatureID,
  setHasUnsavedChanges,
} from "@redux/slices/applicationSlice";

import { formatDate, getTextByLanguage } from "@utils";
import styles from "./ListView.module.css";
import {
  useGetFeaturesForMapQuery,
  useGetMapByIdQuery,
} from "@redux/services/mms";
import { useSetSideSheetInstances } from "../../FeaturesView";

const ListView = () => {
  const { mapID, featureID, hasUnsavedChanges, defaultLanguage } =
    useAppSelector((state) => state.application);
  const dispatch = useAppDispatch();

  const [api, setApi] = useState<GridApi | null>(null);
  const [isUnsavedChangesModalActive, setUnsavedChangesModalActive] =
    useState(false);
  const [localFeatureID, setLocalFeatureID] = useState<string | null>(null);
  const { setGridApi } = useSetSideSheetInstances();

  useEffect(() => {
    dispatch(setFeatureID(null));
  }, [dispatch]);

  if (!mapID) throw new Error("mapID could not be found in the redux state");

  const { isError, isFetching, data } = useGetFeaturesForMapQuery(mapID, {
    skip: !mapID,
  });

  const { data: mapData } = useGetMapByIdQuery(mapID, {
    skip: !mapID,
  });

  useEffect(() => {
    if (isFetching) {
      api?.showLoadingOverlay();
    } else {
      api?.hideOverlay();
      dispatch(setHasUnsavedChanges(false));
    }
  }, [isFetching, api, dispatch]);

  useEffect(() => {
    api?.sizeColumnsToFit();
  }, [featureID, api]);

  if (isError) throw new Error("unable to retrieve map feature data");

  const mapDefaultLanguage = mapData?.languages.default.code;

  const mappedDataToCells = data?.data.map((feature) => {
    return {
      lm_id: feature.id,
      name:
        getTextByLanguage(
          feature.label.name,
          defaultLanguage,
          mapDefaultLanguage,
        ) || "",
      reference:
        getTextByLanguage(
          feature.label.reference,
          defaultLanguage,
          mapDefaultLanguage,
        ) || "",
      category:
        getTextByLanguage(
          feature.categories.category.name,
          defaultLanguage,
          mapDefaultLanguage,
        ) || "",
      subcategory:
        getTextByLanguage(
          feature.categories.subcategory.name,
          defaultLanguage,
          mapDefaultLanguage,
        ) || "",
      last_modified: formatDate(feature.last_modified.modified_at),
      id: feature.id,
      status: feature.status,
    };
  });

  const handleCellClicked = useCallback(
    (event: CellClickedEvent) => {
      if (hasUnsavedChanges) {
        setUnsavedChangesModalActive(true);
        setLocalFeatureID(event.data.id);
        return;
      }

      setLocalFeatureID(null);
      dispatch(setFeatureID(event.data.id));
    },
    [dispatch, hasUnsavedChanges],
  );

  const handleConfirmUnsavedChanges = () => {
    dispatch(setFeatureID(localFeatureID));
    setUnsavedChangesModalActive(false);
  };

  const handleGridReady = (event: GridReadyEvent) => {
    setApi(event.api);
    setGridApi && setGridApi(event.api);
  };

  return (
    <div className={styles.container} data-qa="features-list-view">
      <div className={styles.gridContainer}>
        <Grid
          dataQA="feature-list-grid"
          data={mappedDataToCells}
          onGridReady={handleGridReady}
          columnDefs={[
            {
              field: "lm_id",
              headerName: "LM ID",
              filter: "agTextColumnFilter",
              floatingFilter: true,
              unSortIcon: true,
              onCellClicked: handleCellClicked,
            },
            {
              field: "name",
              headerName: t("views.features.grid_columns.name"),
              filter: "agTextColumnFilter",
              floatingFilter: true,
              unSortIcon: true,
              onCellClicked: handleCellClicked,
            },
            {
              field: "status",
              headerName: t("views.features.grid_columns.status"),
              filter: "agTextColumnFilter",
              floatingFilter: true,
              unSortIcon: true,
              onCellClicked: handleCellClicked,
            },
            {
              field: "category",
              headerName: t("views.features.grid_columns.category"),
              filter: "agTextColumnFilter",
              floatingFilter: true,
              unSortIcon: true,
              onCellClicked: handleCellClicked,
            },
            {
              field: "subcategory",
              headerName: t("views.features.grid_columns.subcategory"),
              filter: "agTextColumnFilter",
              floatingFilter: true,
              unSortIcon: true,
              onCellClicked: handleCellClicked,
            },
            {
              field: "last_modified",
              headerName: t("views.features.grid_columns.last_edited"),
              filter: "agTextColumnFilter",
              floatingFilter: true,
              unSortIcon: true,
              onCellClicked: handleCellClicked,
              // Custom filtering function for the dates, as the formatted dates won't be sorted correctly otherwise
              comparator: (valueA, valueB) => {
                const [dayA, monthA, yearAndTimeA] = valueA.split("/");
                const [dayB, monthB, yearAndTimeB] = valueB.split("/");

                // Days and months need to be swapped around, otherwise new Date() returns the wrong date
                const dateA = new Date(
                  `${monthA}/${dayA}/${yearAndTimeA}`,
                ).toISOString();
                const dateB = new Date(
                  `${monthB}/${dayB}/${yearAndTimeB}`,
                ).toISOString();

                return dateA.localeCompare(dateB);
              },
            },
            {
              cellRenderer: AssetOptions,
              cellClass: "cell-overflow-visible ag-react-container",
              width: 75,
              editable: false,
            },
          ]}
        />
      </div>
      <Dialog
        dataQA="unsaved-changes-dialog"
        isOpen={isUnsavedChangesModalActive}
        onClose={() => setUnsavedChangesModalActive(false)}
        footer={false}
      >
        <UnsavedChanges
          onCancel={() => setUnsavedChangesModalActive(false)}
          onConfirm={handleConfirmUnsavedChanges}
        />
      </Dialog>
    </div>
  );
};

export default ListView;
